import { Pagination } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { HolterRecorderItem } from '@common/interfaces/cardiology/holterRecorderItem';
import { getHolterProcessesAndProcedures } from '@frontend/services/api/holterProcessesApiService';
import { timeoutReloadIntervalMilliseconds, useInterval } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { ActiveOrArchived } from '../../../../../common/enums/activeOrArchived';
import { useSearch } from '../../../../utils/hooks/useSearch';
import { HolterProceduresListHeader } from './HolterProceduresListHeader';
import { HolterProceduresListTable } from './HolterProceduresListTable';

type HolterListSearchState = {
    activeTab: ActiveOrArchived;
    itemsPerPage: number;
    page: number;
};

const defaultSearchState: HolterListSearchState = {
    activeTab: ActiveOrArchived.active,
    itemsPerPage: 10,
    page: 1,
};

export function HolterProceduresAndProcessesList() {
    const abortSignal = useAbortSignal();
    const [holterProcesses, setHolterProcesses] = useState<HolterRecorderItem[]>([]);
    const [totalHolterProcesses, setTotalHolterProcesses] = useState(0);
    const [searchState, setSearchState, pushSearchStateToHistory] =
        useSearch<HolterListSearchState>(defaultSearchState);

    const [isUploading, setIsUploading] = useState(false);
    const [shouldReloadHolterProcesses, setShouldReloadHolterProcesses] = useState(false);

    useEffect(() => {
        loadHolterProcessesAndProcedures();
    }, [searchState]);

    useEffect(() => {
        if (shouldReloadHolterProcesses) {
            loadHolterProcessesAndProcedures();
            setShouldReloadHolterProcesses(false);
        }
    }, [shouldReloadHolterProcesses]);

    useEffect(() => {
        if (holterProcesses.some((process) => process.uploadProgress !== 100)) {
            setIsUploading(true);
        } else {
            setIsUploading(false);
        }
    }, [holterProcesses]);

    useInterval(
        () => {
            loadHolterProcessesAndProcedures();
        },
        isUploading ? timeoutReloadIntervalMilliseconds : null
    );

    return (
        <>
            <HolterProceduresListHeader
                activeTab={searchState.activeTab}
                switchTab={(activeTab) => handleSearch({ activeTab })}
            />
            <HolterProceduresListTable
                activeTab={searchState.activeTab}
                holterProcesses={holterProcesses}
                reloadList={() => setShouldReloadHolterProcesses(true)}
            />

            <Pagination
                onChange={({ page: newPage, pageSize: newPageSize }) => {
                    setSearchState({ itemsPerPage: newPageSize, page: newPage });
                }}
                page={searchState.page}
                pageSize={searchState.itemsPerPage}
                totalItems={totalHolterProcesses}
            />
        </>
    );

    function handleSearch(incomingSearchState: Partial<HolterListSearchState>) {
        const newSearchState = { ...searchState, ...incomingSearchState };
        setSearchState(newSearchState);
        pushSearchStateToHistory(newSearchState);
    }

    async function loadHolterProcessesAndProcedures() {
        const result = await getHolterProcessesAndProcedures(
            searchState.activeTab,
            {
                limit: searchState.itemsPerPage,
                page: searchState.page,
            },
            abortSignal
        );
        if (result?.items) {
            setHolterProcesses(result.items);
            setTotalHolterProcesses(result.total);
        }
    }
}
